import React, { useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';

import BlockComponent from '../components/BlockComponent';
import SavingIndicator from '../components/SavingIndicator';
import { processLayoutBlocks } from '../resources/worksheets';

const Block = ({ block, selected, ...etc }) => {
  const [lastChangedAt, setLastChangedAt] = useState(new Date().getTime());
  const [lastSavedAt, setLastSavedAt] = useState(new Date().getTime());

  const ref = React.useRef();

  React.useEffect(() => {
    window.addEventListener('message', (msg) => {
      if (
        msg.data.type === 'SLED.grading.selectQuestion' &&
        block.uuid === msg.data.questionToSelect &&
        ref.current
      ) {
        ref.current.scrollIntoView({ behavior: 'smooth' });
      }
    });
  }, []);

  const setChangedToNow = useCallback(() => {
    setLastChangedAt(new Date().getTime());
  }, []);

  const setSavedToNow = useCallback(() => {
    setLastSavedAt(new Date().getTime());
  }, []);

  return (
    <div ref={ref}>
      <div style={{ position: 'relative' }}>
        <span
          data-cy="block-meta"
          className="text-black-tint-40 body-s mt0 mb2"
        >
          {block.questionString}
        </span>
        {etc.currentUser.enableSavingIndicators ? (
          <SavingIndicator
            lastChangedAt={lastChangedAt}
            lastSavedAt={lastSavedAt}
          />
        ) : null}
      </div>
      {block.blockComponents.map((blockComponent) => (
        <BlockComponent
          key={blockComponent.uuid}
          {...etc}
          {...blockComponent}
          blockType={block.type}
          cellType={block.cellType}
          readonly={block.readonly}
          setChangedToNow={setChangedToNow}
          setSavedToNow={setSavedToNow}
        />
      ))}
    </div>
  );
};

Block.defaultProps = {
  data: {},
  index: null,
};

Block.propTypes = {
  data: PropTypes.object,
  index: PropTypes.number,
};

const Blocks = ({
  doc,
  data,
  blocks,
  currentUser,
  readOnlyWorksheet,
  isRollup,
  filterQuestion,
  pins,
  changePinState,
  ...etc
}) => {
  const [selected, setSelected] = React.useState();
  const { isGrading, isInstructor } = currentUser;

  const enrichedBlocks = React.useMemo(
    () => processLayoutBlocks(blocks),
    [blocks]
  );

  React.useEffect(() => {
    if (isGrading) {
      // See comments in picasso. This is used by parent frame to establish the right origin for communication.
      window.parent.postMessage({ type: 'SLED.grading.connect' }, '*');
      window.addEventListener('message', (msg) => {
        if (msg.data.type === 'SLED.grading.selectQuestion') {
          setSelected(msg.data.questionToSelect);
        }
      });
    }
  }, []);

  return (
    <>
      {enrichedBlocks
        .filter((block) => !filterQuestion || block.uuid === filterQuestion)
        .map((block) => {
          // Hide instructions in rollup view
          if (isRollup && block.isInstructionsBlock) {
            return;
          }

          const classNames = classnames('block text-black-tint-20', {
            mt3: block.isInstructionsBlock,
            p5: isRollup,
            p3: !isRollup,
            mb3: !isRollup,
          });

          const styles = {};

          if (isRollup && !block.isLastBlock) {
            styles.borderBottom = '1px solid var(--black-tint-90)';
          }

          if (block.isLastBlock) {
            styles.marginBottom = '64px';
          }

          return (
            <div
              data-cy="block"
              className={classNames}
              key={block.uuid}
              style={
                selected === block.uuid
                  ? { ...styles, background: 'var(--yellow-tint-90)' }
                  : styles
              }
              onMouseDown={() => {
                if (isGrading && isInstructor && !block.isInstructionsBlock) {
                  const selectionTarget =
                    selected === block.uuid ? null : block.uuid;

                  window.parent.postMessage(
                    {
                      type: 'SLED.grading.selectionEvent',
                      questionUuid: selectionTarget,
                      questionIndexString: selectionTarget
                        ? block.questionString
                        : null,
                      worksheet: doc.id,
                    },
                    '*'
                  );
                  setSelected(selectionTarget);
                }
              }}
            >
              <Block
                block={block}
                selected={selected === block.uuid}
                key={block.uuid}
                doc={doc}
                data={data}
                currentUser={currentUser}
                readOnlyWorksheet={readOnlyWorksheet}
                pins={pins}
                changePinState={changePinState}
                isRollup={isRollup}
                {...etc}
              />
            </div>
          );
        })}
    </>
  );
};

Blocks.propTypes = {
  readOnlyWorksheet: false,
};

Blocks.propTypes = {
  doc: PropTypes.object.isRequired,
  data: PropTypes.object, // Missing in rollup
  blocks: PropTypes.array.isRequired,
  currentUser: PropTypes.object.isRequired,
  readOnlyWorksheet: PropTypes.bool,
  isRollup: PropTypes.bool,
};

export default Blocks;
